import React, { useState } from 'react';
import { Reveal } from './Reveal';
import { Calculator, Droplets, Leaf, Wallet, TrendingDown } from 'lucide-react';
import { Slider } from './ui/slider';
import { useSection } from '../contexts/ContentContext';

export default function ImpactCalculator() {
  const home = useSection('home');
  const c = home.calculator || {};
  const [bottles, setBottles] = useState([3]);
  const [people, setPeople] = useState([2]);

  const dailyBottles = bottles[0] * people[0];
  const yearlyBottles = dailyBottles * 365;
  const yearlyCO2 = (yearlyBottles * 0.08).toFixed(1); // kg CO2 per bottle
  const yearlySavings = Math.round(yearlyBottles * 0.45); // € per bottle
  const yearlyKm = Math.round(yearlyBottles * 300 / 1000); // km transportation

  return (
    <section className="relative py-20 lg:py-28 bg-gradient-to-br from-sky-50 to-emerald-50 overflow-hidden">
      <div className="absolute -top-20 right-0 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 left-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            {c.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{c.eyebrow}</span>}
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5">
              {c.title || 'Calculez votre'} <span className="gradient-text">{c.titleHighlight || 'empreinte plastique'}</span>
            </h2>
            <p className="text-lg text-slate-600">{c.description}</p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <Reveal>
            <div className="bg-white rounded-3xl shadow-xl border border-sky-100 p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-sky-700" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-sky-950">Votre consommation</h3>
                  <p className="text-sm text-slate-500">Ajustez les curseurs ci-dessous</p>
                </div>
              </div>

              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-sky-900">Bouteilles plastiques / jour / personne</label>
                    <span className="font-display text-2xl font-extrabold text-sky-700">{bottles[0]}</span>
                  </div>
                  <Slider value={bottles} onValueChange={setBottles} min={1} max={8} step={1} className="" />
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>1</span><span>8</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-sky-900">Personnes dans le foyer</label>
                    <span className="font-display text-2xl font-extrabold text-emerald-700">{people[0]}</span>
                  </div>
                  <Slider value={people} onValueChange={setPeople} min={1} max={6} step={1} />
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>1</span><span>6</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-sky-100">
                <p className="text-sm text-slate-600 mb-2">Soit chaque jour :</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold gradient-text">{dailyBottles}</span>
                  <span className="text-slate-600 font-medium">bouteilles plastiques</span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="space-y-4">
              <ResultCard
                icon={Droplets}
                color="from-sky-400 to-sky-600"
                value={yearlyBottles.toLocaleString('fr-FR')}
                unit="bouteilles"
                label="évitées chaque année"
              />
              <ResultCard
                icon={Leaf}
                color="from-emerald-400 to-teal-500"
                value={yearlyCO2}
                unit="kg CO₂"
                label="non rejetés dans l'atmosphère"
              />
              <ResultCard
                icon={Wallet}
                color="from-amber-400 to-orange-500"
                value={`${yearlySavings} €`}
                unit=""
                label="d'économies annuelles pour votre foyer"
              />
              <ResultCard
                icon={TrendingDown}
                color="from-purple-400 to-pink-500"
                value={yearlyKm.toLocaleString('fr-FR')}
                unit="km"
                label="de transport routier évités"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ResultCard({ icon: Icon, color, value, unit, label }) {
  return (
    <div className="bg-white rounded-2xl shadow-md border border-sky-100 p-5 flex items-center gap-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0 shadow-md`}>
        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-3xl font-extrabold text-sky-950">{value}</span>
          {unit && <span className="text-sm font-medium text-slate-500">{unit}</span>}
        </div>
        <p className="text-sm text-slate-600">{label}</p>
      </div>
    </div>
  );
}
